// Client-side error reporting.
//
// Until 2026-08-31 nothing the browser did ever reached us. A student could
// fail eight video hand-ins in a row (Frederiksberg VUC, 2026-08-26) and the
// only artefact anywhere was an orphaned multipart in S3; a lazily-loaded
// modal could fail to render and produce nothing but a console warning the
// user never opens. Every diagnosis started with "ask them to screen-share".
//
// Two paths, on purpose:
//
//   - the RELAY (this module) POSTs to our own API, which emits to PostHog
//     server-side. It is the reliable one: the population most likely to be
//     broken — a school proxy, a TLS-inspecting antivirus, an ad blocker — is
//     also the population least likely to reach a third-party analytics host,
//     and `api.edaptio.com` is by definition reachable if the app is running.
// A browser-side posthog-js was tried alongside it and removed on the same
// day (see git history for d2a82f4ac2 and its revert). The trade in
// posthog-js 1.422.5 turned out to be all-or-nothing: `captureException` is
// implemented by a remotely-served extension, so blocking remote config —
// which is what stops PostHog's project UI from switching on console-log
// capture in a student's browser — also makes the SDK send nothing at all.
// Verified on dev: with the lockdown it made zero network requests. That
// left 183 kB gzipped of dead weight, so the relay is the whole story.
//
// This may never change what the user sees. Everything here is
// fire-and-forget and swallows its own errors.

import axios from 'axios'

/** Hard ceiling per page-session. A render loop must not become a DDoS. */
const MAX_REPORTS_PER_SESSION = 25

/** The same error inside this window is one event, not a thousand. */
const DEDUPE_WINDOW_MS = 60_000

let sentCount = 0
const lastSentAt = new Map()

/**
 * Credentials never leave the browser in a report. Until 2026-10-10 an
 * unhandled axios rejection — which is the RESPONSE object, request config
 * included — was serialised whole, Authorization header and all: 1,013 prod
 * log records carried the bearer tokens of 315 users. The API scrubs the same
 * patterns again (TelemetryRedactor) for bundles still cached in browsers.
 */
const CREDENTIAL_KEY = /^(authorization|x-agent-token|[a-z_-]*token|password|passwd|[a-z_-]*secret|api[_-]?key)$/i

/** Keys whose values describe the request rather than the failure. */
const DROPPED_KEYS = new Set(['config', 'headers', 'request'])

export function redact(text) {
    return String(text)
        .replace(/\bBearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [redacted]')
        .replace(/("(?:authorization|x-agent-token|[a-z_-]*token|password|passwd|[a-z_-]*secret|api[_-]?key)"\s*:\s*)"(?:[^"\\]|\\.)*"/gi, '$1"[redacted]"')
}

/**
 * An axios response (what our API modules reject with) as one readable line:
 * `HTTP 403 GET /assignments/group/855361: wrong group`. Never its config.
 */
function describeHttpResponse(response) {
    const config = response.config || {}
    const method = String(config.method || '').toUpperCase()
    const url = String(config.url || '').split('?')[0]
    const data = response.data
    let detail = ''
    if (data && typeof data === 'object' && typeof data.message === 'string') detail = data.message
    else if (typeof data === 'string') detail = data
    return `HTTP ${response.status}${method ? ` ${method}` : ''}${url ? ` ${url}` : ''}${detail ? `: ${detail.slice(0, 200)}` : ''}`
}

/** Reset for tests. */
export function __resetClientTelemetry() {
    sentCount = 0
    lastSentAt.clear()
}

/**
 * Turn whatever was thrown into something a human can read in PostHog.
 *
 * `String(error.message || error)` was the whole implementation until
 * 2026-09-02, and any rejection that was a plain object — an axios response
 * body, a `{ status, data }` from a store action, a bare DOM event — arrived
 * as the literal text "[object Object]". That was 128 of the first three days'
 * reports across 89 users: the fifth-largest bucket on prod, and every one of
 * them unactionable.
 *
 * Order matters. `message` first so Errors and axios errors keep the shape
 * everything already groups on, then the DOM-event and object cases, and only
 * then String(), which is correct for numbers, symbols and anything carrying a
 * real toString.
 */
export function describeError(error) {
    return redact(describeUnredacted(error))
}

function describeUnredacted(error) {
    if (error === null || error === undefined) return ''
    if (typeof error === 'string') return error
    if (typeof error !== 'object') return String(error)

    if (typeof error.message === 'string' && error.message) return error.message

    if (typeof error.status === 'number' && error.config && typeof error.config === 'object') {
        return describeHttpResponse(error)
    }

    // DOM events (an <img> onerror, a failed XHR) carry no message at all.
    if (typeof Event !== 'undefined' && error instanceof Event) {
        return `${error.type} event${error.target && error.target.src ? ` for ${error.target.src}` : ''}`
    }

    // A custom toString is someone deliberately describing their own error.
    if (error.toString !== Object.prototype.toString) {
        const described = String(error)
        if (described && described !== '[object Object]') return described
    }

    // Last resort: show the object. Bounded, because this ends up in a log
    // line, and best-effort, because it may hold a cycle or a DOM node.
    try {
        const json = JSON.stringify(error, (key, value) => {
            if (DROPPED_KEYS.has(key)) return undefined
            if (CREDENTIAL_KEY.test(key) && typeof value === 'string') return '[redacted]'
            return value
        })
        if (json && json !== '{}') return json.slice(0, 500)
    } catch (stringifyErr) {
        // Circular or non-serialisable — fall through to the key list.
    }

    const keys = Object.keys(error)
    return keys.length ? `unserialisable object {${keys.slice(0, 12).join(', ')}}` : ''
}

function messageOf(error) {
    return describeError(error)
}

/**
 * The path only — never the query string. A `?redirect=` or an OAuth
 * fragment can carry a token, and this is telemetry, not a session log.
 */
function safeLocation() {
    try {
        return `${window.location.pathname}`.slice(0, 300)
    } catch (err) {
        return ''
    }
}

/**
 * Message + first stack frame: enough to group, cheap to compute. Ids in a
 * URL are folded (`/assignments/group/:id`) so one failing endpoint is one
 * group, not one per class.
 */
function fingerprintOf(kind, message, stack) {
    const frame = String(stack || '').split('\n').find((l) => /:\d+:\d+/.test(l)) || ''
    return `${kind}|${message.replace(/\/\d+/g, '/:id').slice(0, 120)}|${frame.trim().slice(0, 120)}`
}

/**
 * Report a client-side error. Safe to call from anywhere, including inside
 * another error handler.
 *
 * @param {string} kind - where it came from: 'window', 'unhandledrejection', 'vue'
 * @param {Error|string} error
 * @param {object} [context] - small, non-PII extras (component name, route)
 */
export function reportClientError(kind, error, context = {}) {
    try {
        if (sentCount >= MAX_REPORTS_PER_SESSION) return

        const message = messageOf(error)
        if (!message) return

        const stack = (error && typeof error === 'object' && error.stack) ? redact(error.stack) : ''
        const fingerprint = fingerprintOf(kind, message, stack)
        const now = Date.now()
        const last = lastSentAt.get(fingerprint)
        if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return

        lastSentAt.set(fingerprint, now)
        sentCount += 1

        // Never awaited, and its own rejection is swallowed — an unhandled
        // one here would come straight back through the unhandledrejection
        // handler that called us.
        axios.post('/client/error', {
            kind,
            message: message.slice(0, 500),
            stack: stack.slice(0, 2000),
            path: safeLocation(),
            fingerprint,
            context,
        }).catch(() => {})
    } catch (reportErr) {
        // Telemetry is not part of any contract.
    }
}
